import { useState, useRef } from "react"
import { Text } from "@radix-ui/themes"
import { MarkdownEditorInput, type MarkdownEditorInputHandle } from "./markdown-editor-input"
import { MarkdownPreview } from "./markdown-preview"
import { projectEmailImagesPath } from "@/routes"

interface MarkdownEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  projectId: string | number
  css: string
}

export function MarkdownEditor({
  value,
  onChange,
  placeholder = "Enter your markdown here...",
  projectId,
  css,
}: MarkdownEditorProps) {
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "success" | "error">(
    "idle",
  )
  const abortControllerRef = useRef<AbortController | null>(null)
  const inputRef = useRef<MarkdownEditorInputHandle>(null)

  const uploadImages = async (files: File[], insertPosition?: number) => {
    if (files.length === 0) return

    const uploadPath = projectEmailImagesPath(projectId)
    setUploadStatus("uploading")
    abortControllerRef.current = new AbortController()

    try {
      const urls: string[] = []

      for (const file of files) {
        const formData = new FormData()
        formData.append("email_image[image]", file)

        const response = await fetch(uploadPath, {
          method: "POST",
          body: formData,
          signal: abortControllerRef.current.signal,
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
        })

        if (response.ok) {
          const data = (await response.json()) as { url: string }
          urls.push(data.url)
        } else {
          throw new Error("Upload failed")
        }
      }

      insertMarkdownImages(urls, insertPosition)
      setUploadStatus("success")
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        setUploadStatus("idle")
      } else {
        console.error("Error uploading images:", error)
        setUploadStatus("error")
      }
    } finally {
      abortControllerRef.current = null
    }
  }

  const insertMarkdownImages = (urls: string[], insertPosition?: number) => {
    const markdownImages = urls.map((url) => `![image](${url})`).join("\n")

    inputRef.current?.insertMarkdownImages(markdownImages, insertPosition)
  }

  const handleCancelUpload = () => {
    if (uploadStatus === "uploading" && abortControllerRef.current) {
      abortControllerRef.current.abort()
      setUploadStatus("idle")
    }
  }

  return (
    <div className="grid min-h-96 grid-cols-2 gap-6">
      <div className="grid grid-rows-[auto_1fr]">
        <Text as="div" size="2" weight="medium" mb="2" color="gray">
          Editor
        </Text>
        <MarkdownEditorInput
          ref={inputRef}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          uploadStatus={uploadStatus}
          onUploadImages={(files, insertPosition) => void uploadImages(files, insertPosition)}
          onCancelUpload={handleCancelUpload}
        />
      </div>
      <div className="grid grid-rows-[auto_1fr]">
        <Text as="div" size="2" weight="medium" mb="2" color="gray">
          Preview
        </Text>
        <MarkdownPreview value={value} css={css} />
      </div>
    </div>
  )
}
